import { computed } from '@angular/core';
import { patchState, signalStoreFeature, type, withComputed, withMethods } from '@ngrx/signals';
import { EntityProps, EntityState } from '@ngrx/signals/entities';

/**
 * Generic pagination feature that can be applied to any store with entities.
 */
export function withPagination<T>() {
  return signalStoreFeature(
    {
      state: type<{ paginator: { pageNb: number; pageSize: number } } & EntityState<T>>(),
      props: type<EntityProps<T>>(),
    },
    withComputed(({ entities, paginator }) => ({
      getPaginated: computed((): T[] =>
        entities().slice((paginator.pageNb() - 1) * paginator.pageSize(), paginator.pageNb() * paginator.pageSize()),
      ),
      totalPageNb: computed(() => Math.ceil(entities().length / paginator.pageSize()) || 1),
      firstItemNumber: computed(() => {
        if (entities().length === 0) {
          return 0;
        }
        return (paginator.pageNb() - 1) * paginator.pageSize() + 1;
      }),
      lastItemNumber: computed(() => {
        const totalPages = Math.ceil(entities().length / paginator.pageSize());
        if (paginator.pageNb() === totalPages) {
          return entities().length;
        }
        return paginator.pageNb() * paginator.pageSize();
      }),
      count: computed(() => entities().length),
    })),
    withMethods((store) => ({
      // Standard pagination methods
      setPageNumber: (pageNb: number) => patchState(store, { paginator: { ...store.paginator(), pageNb } }),

      setPageSize: (pageSize: number) =>
        patchState(store, {
          paginator: { ...store.paginator(), pageSize, pageNb: 1 },
        }),

      // Reset pagination to first page (useful when search/filter changes)
      resetPagination: () => patchState(store, { paginator: { ...store.paginator(), pageNb: 1 } }),
    })),
  );
}
